/**
 * API Service
 *
 * Handles authenticated API requests with JWT token management.
 */

const API_BASE_URL = process.env.REACT_APP_MAIN_API_URL || 'http://localhost:8206';

/**
 * Get the current access token from localStorage
 */
export function getAccessToken() {
  return localStorage.getItem('access_token');
}

/**
 * Get the refresh token from localStorage
 */
export function getRefreshToken() {
  return localStorage.getItem('refresh_token');
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return !!getAccessToken();
}

/**
 * Clear all auth data (logout)
 */
export function clearAuth() {
  localStorage.removeItem('isAuthenticated');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('username');
  localStorage.removeItem('user_id');
  localStorage.removeItem('user_role');
  localStorage.removeItem('preferred_language');
}

/**
 * Refresh the access token using the refresh token
 */
export async function refreshAccessToken() {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    clearAuth();
    throw new Error('Token refresh failed');
  }

  const data = await response.json();
  localStorage.setItem('access_token', data.access_token);

  return data.access_token;
}

/**
 * Make an authenticated API request
 * Automatically refreshes token if expired
 */
export async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add auth header if we have a token
  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let response = await fetch(url, {
    ...options,
    headers,
  });

  // If 401, try to refresh token and retry
  if (response.status === 401 && token) {
    try {
      const newToken = await refreshAccessToken();
      headers['Authorization'] = `Bearer ${newToken}`;

      response = await fetch(url, {
        ...options,
        headers,
      });
    } catch (err) {
      // Refresh failed, user needs to re-login
      window.location.reload();
      throw err;
    }
  }

  return response;
}

/**
 * API methods for chat sessions
 */
export const chatApi = {
  /**
   * Get chat sessions with pagination
   */
  async getSessions(limit = 50, offset = 0) {
    const response = await apiRequest(
      `/api/v1/chat/sessions?limit=${limit}&offset=${offset}`
    );
    if (!response.ok) throw new Error('Failed to fetch sessions');
    return response.json();
  },

  /**
   * Create a new session
   */
  async createSession(title = null, dbId = 'sera_postgres') {
    const response = await apiRequest('/api/v1/chat/sessions', {
      method: 'POST',
      body: JSON.stringify({ title, db_id: dbId }),
    });
    if (!response.ok) throw new Error('Failed to create session');
    return response.json();
  },

  /**
   * Get session with messages
   */
  async getSession(sessionId) {
    const response = await apiRequest(`/api/v1/chat/sessions/${sessionId}`);
    if (!response.ok) throw new Error('Failed to fetch session');
    return response.json();
  },

  /**
   * Update session
   */
  async updateSession(sessionId, updates) {
    const response = await apiRequest(`/api/v1/chat/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    if (!response.ok) throw new Error('Failed to update session');
    return response.json();
  },

  /**
   * Delete session
   */
  async deleteSession(sessionId) {
    const response = await apiRequest(`/api/v1/chat/sessions/${sessionId}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete session');
    return response.json();
  },

  /**
   * Add message to session
   */
  async addMessage(sessionId, message) {
    const response = await apiRequest(
      `/api/v1/chat/sessions/${sessionId}/messages`,
      {
        method: 'POST',
        body: JSON.stringify(message),
      }
    );
    if (!response.ok) throw new Error('Failed to add message');
    return response.json();
  },

  /**
   * Get messages in session
   */
  async getMessages(sessionId, limit = 100) {
    const response = await apiRequest(
      `/api/v1/chat/sessions/${sessionId}/messages?limit=${limit}`
    );
    if (!response.ok) throw new Error('Failed to fetch messages');
    return response.json();
  },
};

/**
 * API methods for user settings
 */
export const settingsApi = {
  /**
   * Get user settings
   */
  async getSettings() {
    const response = await apiRequest('/api/v1/settings');
    if (!response.ok) throw new Error('Failed to fetch settings');
    return response.json();
  },

  /**
   * Update settings (partial)
   */
  async updateSettings(updates) {
    const response = await apiRequest('/api/v1/settings', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    if (!response.ok) throw new Error('Failed to update settings');
    return response.json();
  },

  /**
   * Reset settings to defaults
   */
  async resetSettings() {
    const response = await apiRequest('/api/v1/settings/reset', {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Failed to reset settings');
    return response.json();
  },
};

/**
 * API methods for pinned charts
 */
export const pinnedChartsApi = {
  /**
   * Get user's pinned charts
   */
  async getPinnedCharts(limit = 50, offset = 0) {
    const response = await apiRequest(
      `/api/v1/pinned-charts?limit=${limit}&offset=${offset}`
    );
    if (!response.ok) throw new Error('Failed to fetch pinned charts');
    return response.json();
  },

  /**
   * Pin a chart to dashboard
   */
  async pinChart(chartData) {
    const response = await apiRequest('/api/v1/pinned-charts', {
      method: 'POST',
      body: JSON.stringify(chartData),
    });
    if (!response.ok) throw new Error('Failed to pin chart');
    return response.json();
  },

  /**
   * Get a specific pinned chart
   */
  async getPinnedChart(chartId) {
    const response = await apiRequest(`/api/v1/pinned-charts/${chartId}`);
    if (!response.ok) throw new Error('Failed to fetch pinned chart');
    return response.json();
  },

  /**
   * Update a pinned chart
   */
  async updatePinnedChart(chartId, updates) {
    const response = await apiRequest(`/api/v1/pinned-charts/${chartId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    if (!response.ok) throw new Error('Failed to update pinned chart');
    return response.json();
  },

  /**
   * Unpin a chart
   */
  async unpinChart(chartId) {
    const response = await apiRequest(`/api/v1/pinned-charts/${chartId}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to unpin chart');
    return response.json();
  },

  /**
   * Refresh a pinned chart (re-execute SQL)
   */
  async refreshChart(chartId) {
    const response = await apiRequest(`/api/v1/pinned-charts/${chartId}/refresh`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Failed to refresh chart');
    return response.json();
  },

  /**
   * Reorder pinned charts
   */
  async reorderCharts(chartIds) {
    const response = await apiRequest('/api/v1/pinned-charts/reorder', {
      method: 'POST',
      body: JSON.stringify({ chart_ids: chartIds }),
    });
    if (!response.ok) throw new Error('Failed to reorder charts');
    return response.json();
  },
};

/**
 * API methods for user profile
 */
export const profileApi = {
  /**
   * Get current user profile
   */
  async getProfile() {
    const response = await apiRequest('/api/v1/auth/me');
    if (!response.ok) throw new Error('Failed to fetch profile');
    return response.json();
  },

  /**
   * Update profile
   */
  async updateProfile(updates) {
    const response = await apiRequest('/api/v1/profile', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    if (!response.ok) throw new Error('Failed to update profile');
    return response.json();
  },
};

// Helper methods for common HTTP operations
const get = async (endpoint) => {
  const response = await apiRequest(endpoint, { method: 'GET' });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  return response.json();
};

const post = async (endpoint, data) => {
  const response = await apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  return response.json();
};

export default {
  apiRequest,
  get,
  post,
  chatApi,
  settingsApi,
  profileApi,
  isAuthenticated,
  clearAuth,
  getAccessToken,
};
